import { FC, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { Card, CardHeader, Fab } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

import { UserApiKeyDataSerialized } from '@playq/octopus2-auth';

import { SelectDialog } from '/shared/SelectDialog';
import { ITableChangeQuery, ITableQuery } from '/shared/Table/interfaces';
import { useGenericTablePersistedQuery, useTableQueryChange } from '/hooks';
import { IGenericTableEntity, PersistedQueryKeys } from '/common/models';

import { ApiKeysQueryFields } from '../types';
import { ApiKeysTable } from '../ApiKeysTable';
import { ApiKeyForm } from '../ApiKeyForm';

import { IApiKeysManagerContentProps } from './types';

export const ApiKeysManagerContent: FC<IApiKeysManagerContentProps> = (props) => {
  const {
    keysData,
    isKeyCreationBeingProcessed,
    isKeyRegenerationBeingProcessed,
    isTableProcessing,
    keyDataToCopy,
    defaultKeyAccessLevel,
    tableContentMetadata: { columns, rowActions, rowKeyResolver, data: tableEntries },
    handleApiKeyFormSubmission,
    keyCreationDialogFadingOutHandler,
    tableEntriesSettingHandler,
    isTableProcessingSettingHandler,
  } = props;

  const [keyDialogMode, setKeyDialogMode] = useState<'create' | 'regenerate'>('create');
  const [selectedKeyItem, setSelectedKeyItem] = useState<UserApiKeyDataSerialized>();
  const [isKeyCreationDialogOpened, setIsKeyCreationDialogOpened] = useState<boolean>(false);
  const [query, setQuery] = useGenericTablePersistedQuery({ key: PersistedQueryKeys.ApiKeys });
  const [tableQuery, setTableQuery] = useTableQueryChange({
    tableData: keysData as unknown as IGenericTableEntity[],
    tableEntriesSettingHandler,
    tableQuery: query,
    isTableProcessingSettingHandler,
  });

  const didCancel = useRef<boolean>(false);

  useEffect(() => {
    return () => {
      didCancel.current = true;
    };
  }, []);

  useEffect(() => {
    setTableQuery(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, keysData]);

  const handleQueryChange = (qc: ITableChangeQuery) => {
    setQuery(qc.query);
  };

  const handleCloseRegenerateDialog = () => {
    if (keyDialogMode === 'regenerate') {
      setSelectedKeyItem(undefined);
      setKeyDialogMode('create');
    }
  };
  const isRegenerateCodeModeTitle =
    keyDialogMode === 'regenerate' ? `Regenerate ${selectedKeyItem?.name} key` : 'New Key';
  const keyCreationDialogTitle = !keyDataToCopy ? isRegenerateCodeModeTitle : `Key ${keyDataToCopy?.name}`;
  const keyCreationDialogBody = (
    <ApiKeyForm
      isKeyCreationBeingProcessed={isKeyCreationBeingProcessed}
      isKeyRegenerationBeingProcessed={isKeyRegenerationBeingProcessed}
      defaultKeyAccessLevel={defaultKeyAccessLevel}
      tableQueryToPerformAfterKeyCreation={tableQuery}
      keyFormSubmissionHandler={handleApiKeyFormSubmission}
      keyDataToCopy={keyDataToCopy}
      keyCreationDialogTogglingHandler={setIsKeyCreationDialogOpened}
      mode={keyDialogMode}
      initialValue={selectedKeyItem}
      handleCloseRegenerateDialog={handleCloseRegenerateDialog}
    />
  );

  const handleSelectKey = (_e: SyntheticEvent, item: UserApiKeyDataSerialized) => {
    setKeyDialogMode('regenerate');
    setSelectedKeyItem(item);
    setIsKeyCreationDialogOpened(true);
  };

  const handleDisableBackdropClickDialog = () => {};

  return (
    <Card>
      <CardHeader
        title='API Keys'
        action={
          <Fab color='primary' aria-label='Add' onClick={() => setIsKeyCreationDialogOpened(true)}>
            <AddIcon />
          </Fab>
        }
      />

      <ApiKeysTable
        onRowClick={handleSelectKey}
        columns={columns}
        data={tableEntries}
        actions={rowActions}
        getRowKey={rowKeyResolver}
        pending={true}
        initialQuery={tableQuery as ITableQuery<ApiKeysQueryFields>}
        onQueryChange={handleQueryChange}
        placeholder={{
          text: `There are no any API keys yet. Create a new one.`,
        }}
        options={{ withPagination: true }}
        processing={isTableProcessing}
      />

      <SelectDialog
        open={isKeyCreationDialogOpened}
        onClose={handleDisableBackdropClickDialog}
        title={keyCreationDialogTitle}
        content={keyCreationDialogBody}
        maxWidth='sm'
        TransitionProps={{
          onExited: keyCreationDialogFadingOutHandler,
        }}
      />
    </Card>
  );
};
